import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect, useMemo, useCallback, useId } from 'react';
import { getImageUrl } from '@/utils/images';
import { useLocalization } from '@/hooks/use-localization';
import { NavButton } from './components/nav-button';

const LG_SIZE = 1024;
const MD_SIZE = 768;

type CarouselItem = {
    image?: {
        url: string;
        alt: string;
        width: number;
        height: number;
    };
    link?: {
        url: string;
        rel?: string;
        title: string;
        target?: string;
    };
    title?: string;
    description?: string;
};

export type CarouselProps = {
    slides: CarouselItem[];
    darkMode?: boolean;
    cropImage?: boolean;
    headingLevel?: string;
    numSlidesTablet?: number;
    numSlidesDesktop?: number;
    useZoomAnimation?: boolean;
    alwaysShowDescription?: boolean;
    listLabel?: string;
};

export const Carousel = ({
    slides = [],
    darkMode = false,
    cropImage = true,
    headingLevel = '3',
    numSlidesTablet = 3,
    numSlidesDesktop = 4,
    useZoomAnimation = false,
    alwaysShowDescription = false,
    listLabel,
}: CarouselProps) => {
    const { localize } = useLocalization();
    const desktopRef = useRef<HTMLDivElement | null>(null);
    const defaultRef = useRef<HTMLUListElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const slidesRef = useRef<(HTMLElement | null)[]>([]);
    const hoverTimers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
    const maskPrefix = useId();

    const clearHoverTimer = (index: number) => {
        const timer = hoverTimers.current.get(index);
        if (timer !== undefined) {
            clearTimeout(timer);
            hoverTimers.current.delete(index);
        }
    };

    const [index, setIndex] = useState(0);
    const [active, setActive] = useState(-1);
    const [canGoLeft, setCanGoLeft] = useState(false);
    const [canGoRight, setCanGoRight] = useState(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);

        const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);

    useEffect(
        () => () => {
            for (const timer of hoverTimers.current.values()) {
                clearTimeout(timer);
            }
            hoverTimers.current.clear();
        },
        []
    );

    const numSlides = useMemo(() => slides.length, [slides]);
    const showItems = useMemo(() => Math.min(numSlidesDesktop, numSlides), [numSlidesDesktop, numSlides]);
    const showItemsTablet = useMemo(() => Math.min(numSlidesTablet, numSlides), [numSlidesTablet, numSlides]);
    const showButtons = useMemo(() => numSlides > showItems, [numSlides, showItems]);

    const inactiveSize = useMemo(() => 100 / numSlides, [numSlides]);

    // How much wider the hovered slide gets, as a multiple of its resting width. 1.6 and 1.5 are the
    // ratios the previous hardcoded sizes worked out to for the 4-up and 3-up layouts.
    const growth = useMemo(() => (showItems <= 1 ? 1 : showItems === 4 ? 1.6 : 1.5), [showItems]);

    const bigSize = useMemo(() => inactiveSize * growth, [inactiveSize, growth]);

    // The slides on screen must still add up to exactly one screenful, so the shrink is shared by the
    // other *visible* slides. Off-screen slides keep inactiveSize and are not part of this.
    const smallSize = useMemo(
        () => (showItems > 1 ? (showItems * inactiveSize - bigSize) / (showItems - 1) : inactiveSize),
        [showItems, inactiveSize, bigSize]
    );

    const totalSize = useMemo(() => (100 / showItems) * numSlides, [numSlides, showItems]);

    const getItemHeightClasses = (item: CarouselItem) => {
        const classes: string[] = [];
        const hasLink = !!item?.link?.url && !!item.link?.title;
        const descriptionLength = item?.description?.length || 0;

        if (!descriptionLength || (!useZoomAnimation && alwaysShowDescription)) {
            return classes;
        }

        if (numSlidesDesktop === 4) {
            if (descriptionLength > 250) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[660px]'
                            : 'lg:r-min-h-[620px]'
                        : hasLink
                          ? 'lg:r-min-h-[550px]'
                          : 'lg:r-min-h-[520px]'
                );
            } else if (descriptionLength > 200) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[610px]'
                            : 'lg:r-min-h-[580px]'
                        : hasLink
                          ? 'lg:r-min-h-[540px]'
                          : 'lg:r-min-h-[500px]'
                );
            } else if (descriptionLength > 100) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[590px]'
                            : 'lg:r-min-h-[560px]'
                        : hasLink
                          ? 'lg:r-min-h-[510px]'
                          : 'lg:r-min-h-[490px]'
                );
            } else {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[490px]'
                            : 'lg:r-min-h-[460px]'
                        : hasLink
                          ? 'lg:r-min-h-[495px]'
                          : 'lg:r-min-h-[470px]'
                );
            }
        } else {
            if (descriptionLength > 250) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[630px]'
                            : 'lg:r-min-h-[590px]'
                        : hasLink
                          ? 'lg:r-min-h-[530px]'
                          : 'lg:r-min-h-[500px]'
                );
            } else if (descriptionLength > 200) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[585px]'
                            : 'lg:r-min-h-[555px]'
                        : hasLink
                          ? 'lg:r-min-h-[520px]'
                          : 'lg:r-min-h-[480px]'
                );
            } else if (descriptionLength > 100) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[560px]'
                            : 'lg:r-min-h-[535px]'
                        : hasLink
                          ? 'lg:r-min-h-[510px]'
                          : 'lg:r-min-h-[480px]'
                );
            } else {
                classes.push(
                    alwaysShowDescription ? (hasLink ? 'r-min-h-[510px]' : 'r-min-h-[480px]') : hasLink ? 'r-min-h-[490px]' : 'r-min-h-[450px]'
                );
            }
        }

        return classes;
    };

    function getItemClasses(largeWithZoom = false, item: CarouselItem) {
        const classes = getItemHeightClasses(item);

        if (numSlidesTablet > 2 && numSlides > 2) {
            classes.push('md:r-min-w-[calc(33%-0.75rem)]');
        } else {
            classes.push('md:r-min-w-[calc(50%-0.75rem)]');
        }

        if (largeWithZoom) {
            return classes;
        }

        if (numSlidesDesktop > 3 && numSlides > 3) {
            classes.push('xl:r-min-w-[calc(25%-1.25rem)]');
        } else {
            classes.push('xl:r-min-w-[calc(33%-0.75rem)]');
        }

        return classes;
    }

    function getHeaderTag() {
        switch (headingLevel) {
            case '2':
                return 'h2';
            case '3':
                return 'h3';
            case '4':
                return 'h4';
            case '5':
                return 'h5';
            case '6':
                return 'h6';
        }

        return 'h3';
    }

    const onMouseOver = (index: number) => {
        if (index !== active) {
            setActive(index);
        }

        const slide = slidesRef.current?.[index] ?? null;
        if (!slide) {
            return;
        }

        slide.classList.add('hovering');

        clearHoverTimer(index);
        hoverTimers.current.set(
            index,
            setTimeout(() => {
                hoverTimers.current.delete(index);
                if (slides[index].description?.length) {
                    slide.classList.add('finished-animating');
                }
            }, 230)
        );
    };

    const onMouseOut = (index: number) => {
        if (index === active) {
            setActive(-1);
        }

        const slide = slidesRef.current?.[index] ?? null;
        if (!slide) {
            return;
        }

        clearHoverTimer(index);

        slide.classList.remove('hovering');
        if (slides[index].description?.length) {
            slide.classList.remove('finished-animating');
        }
    };

    const getItems = (largeWithZoom = false) =>
        slides.map((item, itemIndex) => {
            const inViewport = itemIndex >= index && itemIndex < index + showItems;
            const style: React.CSSProperties = {};
            const buffer = 24;
            const maskId = `mask${maskPrefix}${itemIndex}_${largeWithZoom ? 'zoom' : 'default'}`;

            if (largeWithZoom) {
                style.minWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                if (!prefersReducedMotion) {
                    style.transition = 'min-width ease-in-out 230ms';
                }
                if (!inViewport) {
                    style.minWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                    style.maxWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                    delete style.transition;
                } else if (!prefersReducedMotion) {
                    if (itemIndex === active && useZoomAnimation) {
                        style.minWidth = `calc(${bigSize}% - ${buffer}px)`;
                    } else if (active !== -1) {
                        style.minWidth = `calc(${smallSize}% - ${buffer}px)`;
                    }
                }
            }

            const HeaderTag = getHeaderTag();

            const wrapperClassName =
                'no-icon r-relative r-group r-flex r-flex-1 r-flex-col r-overflow-hidden r-rounded-xl focus-within-double-outline';
            const wrapperHandlers = {
                onMouseLeave: () => onMouseOut(itemIndex),
                onMouseEnter: () => onMouseOver(itemIndex),
                onFocus: () => onMouseOver(itemIndex),
                onBlur: () => onMouseOut(itemIndex),
            };

            const itemContent = (
                <>
                    <div
                        style={{ backgroundImage: `url(${getImageUrl(item.image?.url)})` }}
                        className={clsx(
                            'r-max-h-[350px] r-min-h-[350px] r-w-full r-flex-1 r-bg-center r-bg-no-repeat r-p-2',
                            cropImage ? 'r-rounded-xl r-bg-cover' : 'r-bg-contain'
                        )}
                    />
                    <div className="r-pt-4">
                        <HeaderTag className="r-pb-1 r-text-lg r-font-medium">{item.title}</HeaderTag>
                        <div
                            className={clsx({
                                'r-overflow-hidden r-transition-all r-duration-200 lg:r-max-h-0 lg:r-opacity-0':
                                    useZoomAnimation && !alwaysShowDescription && !prefersReducedMotion,
                                'carousel-description-animated': active === itemIndex && !prefersReducedMotion,
                            })}
                        >
                            <p
                                className={clsx('r-text-sm', largeWithZoom && 'lg:r-line-clamp-4 lg:r-min-h-[5rem] lg:r-max-w-[19rem]')}
                                dangerouslySetInnerHTML={{ __html: item.description || '' }}
                            />
                        </div>
                        {item.link?.url && item.link?.title && (
                            <div className={clsx(largeWithZoom && 'lg:r-min-h-[3.5rem]')}>
                                <Link
                                    to={item.link.url}
                                    target={item.link.target || '_self'}
                                    rel={item.link.rel || ''}
                                    className="custom-focus-indicator no-icon r-flex r-items-center r-gap-2 r-pt-2 r-text-base after:r-absolute after:r-inset-0 after:r-z-10 after:r-content-['']"
                                >
                                    {item.link?.title}{' '}
                                    <svg
                                        aria-hidden="true"
                                        focusable="false"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="17"
                                        viewBox="0 0 14 17"
                                        fill="none"
                                    >
                                        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="17">
                                            <path d="M14 0.5H0V16.5H14V0.5Z" fill="white" />
                                        </mask>
                                        <g mask={`url(#${maskId})`}>
                                            <path
                                                d="M13.7062 9.207L8.70618 14.207C8.51245 14.4039 8.25618 14.5008 7.99998 14.5008C7.74372 14.5008 7.48805 14.4031 7.29305 14.2078C6.90245 13.8172 6.90245 13.1844 7.29305 12.7937L10.5875 9.5008H0.999949C0.447762 9.5008 0 9.05393 0 8.5008C0 7.94766 0.447762 7.5008 0.999949 7.5008H10.5875L7.29372 4.20703C6.90305 3.8164 6.90305 3.18359 7.29372 2.79296C7.68432 2.40235 8.31712 2.40235 8.70778 2.79296L13.7078 7.793C14.0968 8.18513 14.0968 8.8164 13.7062 9.207Z"
                                                fill={darkMode ? '#fff' : '#141619'}
                                            />
                                        </g>
                                    </svg>
                                </Link>
                            </div>
                        )}
                    </div>
                </>
            );

            const innerRef = (el: HTMLElement | null) => {
                if (largeWithZoom) {
                    slidesRef.current[itemIndex] = el;
                }
            };

            return (
                <li
                    key={itemIndex}
                    style={style}
                    tabIndex={-1}
                    onFocus={() => onMouseOver(itemIndex)}
                    onBlur={() => onMouseOut(itemIndex)}
                    className={clsx(
                        'carousel-item r-snap-start r-p-1 r-outline-none max-md:r-min-w-[80vw] max-md:r-pl-4 lg:r-w-full',
                        getItemClasses(largeWithZoom, item)
                    )}
                >
                    <div ref={innerRef} className={wrapperClassName} {...wrapperHandlers}>
                        {itemContent}
                    </div>
                </li>
            );
        });

    function getCurrentRef() {
        if (useZoomAnimation) {
            if (!desktopRef?.current || !containerRef?.current || !defaultRef?.current) {
                return null;
            }

            return containerRef!.current!.offsetWidth >= LG_SIZE ? desktopRef!.current! : defaultRef!.current!;
        }

        return defaultRef!.current || null;
    }

    function getScrollAmount(ref: HTMLElement) {
        if (ref.clientWidth < MD_SIZE) {
            return ref.clientWidth;
        }

        const numItems = ref.clientWidth >= LG_SIZE ? showItems : showItemsTablet;
        return ref.clientWidth / numItems;
    }

    const slide = (direction: number) => {
        if ((direction > 0 && !canGoRight) || (direction < 0 && !canGoLeft)) {
            return;
        }

        const ref = getCurrentRef();
        if (!ref) {
            return;
        }

        const scrollAmount = getScrollAmount(ref);
        ref.scrollBy({ left: direction * scrollAmount + 16 * direction + 1 * direction, behavior: 'smooth' });
    };

    const handleScroll = () => {
        const ref = getCurrentRef();
        if (!ref || !containerRef?.current) {
            return;
        }

        const itemWidth = containerRef.current.offsetWidth / showItems;

        // Set the correct active item so we can calculate later what's offscreen.
        // We do this on scroll so that even when someone scrolls, we know where we are.
        setIndex(Math.floor(Math.ceil(ref.scrollLeft) / Math.ceil(itemWidth)));
    };

    useEffect(() => {
        desktopRef.current?.addEventListener('scroll', handleScroll);
        defaultRef.current?.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            desktopRef.current?.removeEventListener('scroll', handleScroll);
            defaultRef.current?.removeEventListener('scroll', handleScroll);
        };
    }, [desktopRef, containerRef]);

    // Use IntersectionObserver to determine arrow visibility by watching the first and last slides.
    // This is more reliable than scrollWidth math, which suffers from sub-pixel rounding issues
    // and comparing widths across different DOM elements (scroll container vs outer container).
    const updateArrowVisibility = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            for (const entry of entries) {
                const isFullyVisible = entry.intersectionRatio >= 0.95;
                if (entry.target.getAttribute('data-carousel-edge') === 'first') {
                    setCanGoLeft(!isFullyVisible);
                }
                if (entry.target.getAttribute('data-carousel-edge') === 'last') {
                    setCanGoRight(!isFullyVisible);
                }
            }
        },
        [setCanGoLeft, setCanGoRight]
    );

    useEffect(() => {
        const observers: IntersectionObserver[] = [];

        const setupObserver = (scrollContainer: HTMLElement | null) => {
            if (!scrollContainer) return;

            const items = scrollContainer.querySelectorAll('li');
            if (items.length === 0) return;

            const firstItem = items[0];
            const lastItem = items[items.length - 1];
            firstItem.setAttribute('data-carousel-edge', 'first');
            lastItem.setAttribute('data-carousel-edge', 'last');

            const observer = new IntersectionObserver(updateArrowVisibility, {
                root: scrollContainer,
                threshold: [0, 0.5, 0.95, 1],
            });

            observer.observe(firstItem);
            observer.observe(lastItem);
            observers.push(observer);
        };

        setupObserver(defaultRef.current);
        if (useZoomAnimation) {
            setupObserver(desktopRef.current);
        }

        return () => {
            for (const observer of observers) {
                observer.disconnect();
            }
        };
    }, [defaultRef, desktopRef, useZoomAnimation, updateArrowVisibility, slides.length]);

    const desktopButtonBase = 'max-md:r-hidden r-absolute r-top-1/2 r--translate-y-[68px] r-z-20 r-p-2';

    const leftButtonClasses = clsx(desktopButtonBase, 'r-left-4 lg:-r-left-6 r-translate-x-7 lg:r-translate-x-10');

    const rightButtonClasses = clsx(desktopButtonBase, 'r-right-4 lg:-r-right-6 r--translate-x-7 lg:r--translate-x-10');

    return (
        <div className={clsx({ 'r-text-white': darkMode })}>
            <div
                ref={containerRef}
                className={clsx('r-flex max-lg:r-pb-8', {
                    'md:r-overflow-hidden': !useZoomAnimation,
                })}
            >
                {showButtons && (
                    <NavButton
                        direction="prev"
                        onClick={() => slide(-1)}
                        disabled={!canGoLeft}
                        label={localize('carousel-prev')}
                        className={leftButtonClasses}
                    />
                )}

                <ul
                    ref={defaultRef}
                    // Safari/VoiceOver issue. When list-style: none is applied via CSS, VoiceOver removes list semantics. Therefore we explicitly add
                    role="list"
                    aria-label={listLabel || localize('carousel-list')}
                    aria-hidden={useZoomAnimation ? 'true' : 'false'}
                    className={clsx(
                        'scrollbar-hide r-flex r-w-full r-snap-x r-snap-mandatory r-overflow-x-auto r-transition-all r-duration-300 max-md:-r-mx-4 max-md:r-min-w-[100vw] max-md:r-max-w-[100vw] max-md:r-pr-6 max-md:last:r-pr-4 md:r-gap-6',
                        {
                            'lg:r-hidden': useZoomAnimation,
                        }
                    )}
                >
                    {getItems()}
                </ul>

                {useZoomAnimation && (
                    <div className="scrollbar-hide r-w-full r-snap-x r-snap-mandatory r-overflow-x-scroll max-lg:r-hidden" ref={desktopRef}>
                        <ul
                            role="list"
                            aria-label={listLabel || localize('carousel-list')}
                            aria-hidden="false"
                            style={{ width: `calc(${totalSize}%)` }}
                            className="r-hidden r-snap-x r-snap-start r-gap-6 r-scroll-smooth lg:r-flex"
                        >
                            {getItems(true)}
                        </ul>
                    </div>
                )}

                {showButtons && (
                    <NavButton
                        direction="next"
                        onClick={() => slide(1)}
                        disabled={!canGoRight}
                        label={localize('carousel-next')}
                        className={rightButtonClasses}
                    />
                )}
            </div>

            {/* Mobile navigation buttons - outside flex container */}
            <div className="r-flex r-w-full r-justify-center r-gap-4 r-pt-4 md:r-hidden">
                <NavButton direction="prev" onClick={() => slide(-1)} disabled={!canGoLeft} label={localize('carousel-prev')} className="r-p-3" />
                <NavButton direction="next" onClick={() => slide(1)} disabled={!canGoRight} label={localize('carousel-next')} className="r-p-3" />
            </div>
        </div>
    );
};
